import { useQuery } from '@apollo/client/react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLanguage } from './useLanguage';
import { useMarket } from './useMarketZustand';
import { shopifyClient } from '../lib/shopify-client';

// Helper function to convert language codes to Shopify LanguageCode enum
function getShopifyLanguageCode(lang: string): string {
  const langMap: Record<string, string> = {
    en: 'EN',
    de: 'DE',
    pl: 'PL',
    uk: 'UK',
  };
  return langMap[lang] || 'EN';
}

// Helper function to convert market codes to Shopify CountryCode enum
function getShopifyCountryCode(market: string): string {
  const countryMap: Record<string, string> = {
    de: 'DE',
    pl: 'PL',
  };
  return countryMap[market] || 'DE';
}

// Product collection mapping based on categorization data
function getProductCollections(productId: string, productTitle: string): string[] {
  // Create basic mapping based on product characteristics and titles
  const collections: string[] = [];
  
  const titleLower = productTitle.toLowerCase();
  
  // Basic categorization based on product title keywords
  if (titleLower.includes('organic')) {
    collections.push('organic-spices');
  }
  
  if (titleLower.includes('chilli') || titleLower.includes('chili') || titleLower.includes('pepper')) {
    collections.push('hot-peppers-and-chili');
  }
  
  if (titleLower.includes('cardamom') || titleLower.includes('cloves') || titleLower.includes('cinnamon')) {
    collections.push('spices-and-herbs');
  }
  
  if (titleLower.includes('masala') || titleLower.includes('curry') || titleLower.includes('garam')) {
    collections.push('indian-cuisine', 'spice-blends');
  }
  
  if (titleLower.includes('kebab') || titleLower.includes('bbq')) {
    collections.push('for-grill-bbq');
  }
  
  if (titleLower.includes('steak') || titleLower.includes('beef')) {
    collections.push('for-steak');
  }
  
  if (titleLower.includes('basil') || titleLower.includes('oregano')) {
    collections.push('herbs', 'italian-cuisine');
  }
  
  if (titleLower.includes('salt')) {
    collections.push('salts');
  }
  
  if (titleLower.includes('chia') || titleLower.includes('seeds')) {
    collections.push('seeds-and-nuts');
  }
  
  // Default to spices and herbs if no specific category found
  if (collections.length === 0) {
    collections.push('spices-and-herbs');
  }
  
  return collections;
}
import {
  GET_PRODUCTS,
  GET_PRODUCT,
  SEARCH_PRODUCTS,
  GET_PRODUCTS_PAGINATED,
  GET_BLOGS,
  GET_RECIPES,
  GET_RECIPE,
  GET_COLLECTIONS,
  GET_COLLECTION_PRODUCTS,
} from '../lib/shopify-queries';
import type {
  ShopifyProduct,
  ShopifyProductsResponse,
  ShopifyProductResponse,
  ShopifyProductsPaginatedResponse,
  ShopifyArticle,
  ShopifyRecipesResponse,
  ShopifyRecipeResponse,
} from '../lib/shopify-client';
import type { Product } from '../components/ProductCard';
import { logger } from '../utils/logger';

// Shopify collection types
export interface ShopifyCollection {
  id: string;
  title: string;
  handle: string;
  description: string;
  image?: {
    url: string;
    altText: string;
  };
  products: {
    edges: Array<{
      node: {
        id: string;
        title: string;
        handle: string;
      };
    }>;
  };
}

export interface ShopifyCollectionsResponse {
  collections: {
    edges: Array<{
      node: ShopifyCollection;
    }>;
  };
}

// Blog post interface matching RecipesPage expectations
interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  image: string;
  author: string;
  readTime: string;
  date: string;
  category: string;
  featured?: boolean;
  rating?: number;
  isRecipe?: boolean;
  handle?: string;
}

// Recipe interface matching RecipesPage expectations
interface Recipe {
  id: string;
  title: string;
  description: string;
  image: string;
  author: string;
  prepTime: number;
  cookTime: number;
  totalTime: number;
  servings: number;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  cuisine: string;
  rating: number;
  reviewCount: number;
  calories: number;
  tags: string[];
  datePublished: string;
  mealType: string[];
  dietaryRestrictions: string[];
  featured?: boolean;
  isNew?: boolean;
  handle: string;
}

// Convert Shopify article to BlogPost format
export function convertShopifyArticleToBlogPost(
  article: ShopifyArticle
): BlogPost {
  // Calculate estimated read time based on content length
  const wordCount = article.content ? article.content.split(/\s+/).length : 0;
  const readTime = Math.max(1, Math.ceil(wordCount / 200)); // Average reading speed: 200 words/minute

  return {
    id: article.id,
    title: article.title,
    excerpt:
      article.excerpt ||
      article.content.substring(0, 200) + '...' ||
      'No description available',
    image: article.image?.url || '/recipe_fb.png', // Fallback recipe image
    author: article.author
      ? `${article.author.firstName} ${article.author.lastName}`.trim() ||
        'Olispices Chef'
      : 'Olispices Chef',
    readTime: `${readTime} min read`,
    date: new Date(article.publishedAt).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }),
    category: article.tags.length > 0 ? article.tags[0] : 'Recipe',
    featured: article.tags.includes('featured'),
    rating: article.tags.includes('5-star')
      ? 5
      : article.tags.includes('4-star')
        ? 4
        : undefined,
    isRecipe: true, // All blog articles are treated as recipes
    handle: article.handle,
  };
}

// Helper function to get metafield value
function getArticleMetafieldValue(metafields: ShopifyArticle['metafields'], namespace: string, key: string): string | null {
  if (!metafields) return null;
  
  const metafield = metafields.find(m => 
    m && m.namespace === namespace && m.key === key
  );
  return metafield?.value || null;
}

// Convert Shopify article to Recipe format (for RecipesPage) - STRICT: only metafields, no fallbacks
export function convertShopifyArticleToRecipe(article: ShopifyArticle): Recipe | null {
  // Extract recipe metadata from metafields ONLY - using 'custom' namespace
  const difficultyMeta = getArticleMetafieldValue(article.metafields, 'custom', 'difficulty');
  const prepTimeMeta = getArticleMetafieldValue(article.metafields, 'custom', 'prep_time');
  const servingsMeta = getArticleMetafieldValue(article.metafields, 'custom', 'portions');
  const ratingMeta = getArticleMetafieldValue(article.metafields, 'custom', 'rating_average');
  const reviewCountMeta = getArticleMetafieldValue(article.metafields, 'custom', 'rating_count');
  const caloriesMeta = getArticleMetafieldValue(article.metafields, 'custom', 'calories');
  const proteinMeta = getArticleMetafieldValue(article.metafields, 'custom', 'protein');
  const fatMeta = getArticleMetafieldValue(article.metafields, 'custom', 'fat');
  const carbsMeta = getArticleMetafieldValue(article.metafields, 'custom', 'carbs');
  const tagsMeta = getArticleMetafieldValue(article.metafields, 'custom', 'tags');
  const descriptionMeta = getArticleMetafieldValue(article.metafields, 'custom', 'description');
  const processedMeta = getArticleMetafieldValue(article.metafields, 'custom', 'processed');

  // If essential recipe data is missing, return null (no recipe)
  if (!difficultyMeta || !prepTimeMeta || !servingsMeta || processedMeta !== 'true') {
    return null;
  }

  // Parse difficulty (strict) - 'normal' maps to 'Medium'
  const difficultyLower = difficultyMeta.toLowerCase();
  const difficulty: 'Easy' | 'Medium' | 'Hard' = 
    difficultyLower === 'hard' ? 'Hard' : 
    difficultyLower === 'normal' ? 'Medium' : 
    difficultyLower === 'easy' ? 'Easy' : 
    'Medium';

  // Parse meal types from tags
  let mealType: string[] = [];
  let dietaryRestrictions: string[] = [];
  let cuisine = 'International';
  
  if (tagsMeta) {
    try {
      const tags = JSON.parse(tagsMeta);
      const mealOptions = ['breakfast', 'lunch', 'dinner', 'appetizer', 'dessert', 'snack', 'main course'];
      const dietOptions = ['vegetarian', 'vegan', 'gluten-free', 'dairy-free', 'low carb', 'ketogenic', 'paleo'];
      const cuisineOptions = ['turkish', 'italian', 'indian', 'mediterranean', 'asian', 'european', 'american'];
      
      mealType = tags.filter((tag: string) => 
        mealOptions.some(meal => tag.toLowerCase().includes(meal.toLowerCase()))
      );
      dietaryRestrictions = tags.filter((tag: string) => 
        dietOptions.some(diet => tag.toLowerCase().includes(diet.toLowerCase()))
      );
      const foundCuisine = tags.find((tag: string) => 
        cuisineOptions.some(c => tag.toLowerCase().includes(c.toLowerCase()))
      );
      if (foundCuisine) cuisine = foundCuisine;
    } catch {
      // Keep defaults
    }
  }

  // Parse times (prep_time is like "20 Min.")
  const prepTimeMatch = prepTimeMeta.match(/\d+/);
  const prepTime = prepTimeMatch ? parseInt(prepTimeMatch[0]) : 0;
  const cookTime = 0; // No cook time in our data
  const totalTime = prepTime;

  // Parse review count ("(18 Reviews)" -> 18)
  const reviewCountMatch = reviewCountMeta?.match(/\d+/);
  const reviewCount = reviewCountMatch ? parseInt(reviewCountMatch[0]) : 0;
  
  return {
    id: article.id,
    handle: article.handle,
    title: article.title,
    description: descriptionMeta || article.excerpt || article.content.substring(0, 300) + '...',
    image: article.image?.url || '/recipe_fb.png',
    author: article.author
      ? `${article.author.firstName} ${article.author.lastName}`.trim() || 'Chef'
      : 'Chef',
    prepTime,
    cookTime,
    totalTime,
    servings: parseInt(servingsMeta) || 1,
    difficulty,
    cuisine: cuisine.charAt(0).toUpperCase() + cuisine.slice(1),
    rating: ratingMeta ? parseFloat(ratingMeta) : 0,
    reviewCount,
    calories: caloriesMeta ? parseInt(caloriesMeta) : 0,
    tags: article.tags,
    datePublished: new Date(article.publishedAt).toISOString(),
    mealType: mealType.length > 0 ? mealType : ['Main Course'],
    dietaryRestrictions,
    featured: false, // No featured flag in our data
    isNew: new Date(article.publishedAt) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
  };
}

// Calculate accurate price per kg based on weight and unit
function calculatePricePerKg(
  price: number,
  weight: string,
  unit: string
): number {
  const weightNum = parseFloat(weight);

  if (isNaN(weightNum) || weightNum <= 0) {
    return price; // Fallback if weight parsing fails
  }

  // Convert to kg based on unit
  let weightInKg: number;

  switch (unit.toLowerCase()) {
    case 'kg':
      weightInKg = weightNum;
      break;
    case 'g':
    case '': // Default to grams if no unit specified
      weightInKg = weightNum / 1000;
      break;
    case 'l':
    case 'liter':
    case 'litre':
      // For liquids, assume 1L ≈ 1kg density
      weightInKg = weightNum;
      break;
    case 'ml':
      // For liquids, assume 1000ml ≈ 1kg
      weightInKg = weightNum / 1000;
      break;
    default:
      // Unknown unit, assume grams
      weightInKg = weightNum / 1000;
      // Unknown unit, assuming grams
      break;
  }

  // Calculate price per kg
  const pricePerKg = price / weightInKg;

  // Round to 2 decimal places
  return Math.round(pricePerKg * 100) / 100;
}

// Helper function to extract metafield value
function getMetafieldValue(metafields: ShopifyProduct['metafields'], namespace: string, key: string): string | null {
  if (!metafields) return null;
  
  const metafield = metafields.find(m => 
    m && m.namespace === namespace && m.key === key
  );
  return metafield?.value || null;
}

// Convert Shopify product to our internal Product interface
export function convertShopifyProduct(shopifyProduct: ShopifyProduct): Product {
  const firstImage = shopifyProduct.images?.edges?.[0]?.node?.url || '';
  const price = parseFloat(shopifyProduct.priceRange.minVariantPrice.amount);

  // Extract metafields
  const shortDescription = getMetafieldValue(shopifyProduct.metafields, 'product', 'short_description');
  const ingredients = getMetafieldValue(shopifyProduct.metafields, 'product', 'ingredients');
  const usageTips = getMetafieldValue(shopifyProduct.metafields, 'product', 'usage_tips');
  const allergens = getMetafieldValue(shopifyProduct.metafields, 'allergens', 'contains');
  const rating = getMetafieldValue(shopifyProduct.metafields, 'nutrition', 'rating');
  const reviewCount = getMetafieldValue(shopifyProduct.metafields, 'product', 'review_count');
  const countryOrigin = getMetafieldValue(shopifyProduct.metafields, 'properties', 'country_origin');
  const organic = getMetafieldValue(shopifyProduct.metafields, 'properties', 'organic');
  
  // Extract nutrition data
  const calories = getMetafieldValue(shopifyProduct.metafields, 'nutrition', 'energy_kcal');
  const protein = getMetafieldValue(shopifyProduct.metafields, 'nutrition', 'protein_g');
  const totalFat = getMetafieldValue(shopifyProduct.metafields, 'nutrition', 'fat_total_g');
  const carbohydrates = getMetafieldValue(shopifyProduct.metafields, 'nutrition', 'carbs_total_g');
  const fiber = getMetafieldValue(shopifyProduct.metafields, 'nutrition', 'carbs_fiber_g');
  const sodium = getMetafieldValue(shopifyProduct.metafields, 'nutrition', 'sodium_mg');
  
  // Extract active compounds (JSON array)
  const activeCompoundsRaw = getMetafieldValue(shopifyProduct.metafields, 'health', 'active_compounds');
  const activeCompounds = activeCompoundsRaw ? 
    (activeCompoundsRaw.startsWith('[') ? JSON.parse(activeCompoundsRaw) : [activeCompoundsRaw]) : 
    [];
  
  // Extract health benefits (JSON array)
  const healthBenefitsRaw = getMetafieldValue(shopifyProduct.metafields, 'health', 'health_benefits');
  const healthBenefits = healthBenefitsRaw ? 
    (healthBenefitsRaw.startsWith('[') ? JSON.parse(healthBenefitsRaw) : [healthBenefitsRaw]) : 
    [];

  // Get collections from product categorization data
  const collections = getProductCollections(shopifyProduct.id, shopifyProduct.title);

  return {
    id: shopifyProduct.id,
    handle: shopifyProduct.handle,
    name: shopifyProduct.title,
    description: shopifyProduct.description || '',
    shortDescription: shortDescription || '',
    ingredients: ingredients || '',
    usageTips: usageTips || '',
    allergens: allergens || '',
    nutritionFacts: {
      calories: calories ? parseFloat(calories) : undefined,
      protein: protein ? parseFloat(protein) : undefined,
      totalFat: totalFat ? parseFloat(totalFat) : undefined,
      carbohydrates: carbohydrates ? parseFloat(carbohydrates) : undefined,
      fiber: fiber ? parseFloat(fiber) : undefined,
      sodium: sodium ? parseFloat(sodium) : undefined,
    },
    activeCompounds: activeCompounds,
    healthBenefits: healthBenefits,
    price: price,
    image: firstImage,
    rating: rating ? parseFloat(rating) : 4.5,
    reviewCount: reviewCount ? parseInt(reviewCount) : 0,
    tags: shopifyProduct.tags || [],
    collections: collections,
    isNew: false, // Could be determined by creation date
    isBestseller: false, // Could be from metafields
    origin: countryOrigin || 'Premium Quality',
    isCertifiedOrganic: organic === 'Y',
    // Generate weight variants from Shopify variants if available
    weightVariants:
      shopifyProduct.variants?.edges?.map((edge, index) => {
        const variantTitle = edge.node.title || `${(index + 1) * 100}g`;
        // Extract weight and unit from title (e.g., "100g" -> weight: "100", unit: "g")
        const match = variantTitle.match(/^(\d+)([a-zA-Z]*)$/);
        const weight = match ? match[1] : variantTitle;
        const unit = match ? match[2] : '';
        const price = parseFloat(edge.node.price.amount);

        // Calculate accurate price per kg based on actual weight and unit
        const pricePerKg = calculatePricePerKg(price, weight, unit);

        return {
          weight,
          unit,
          price,
          pricePerKg,
        };
      }) || [],
  };
}

// Hook for getting all products with language support
export function useShopifyProducts(limit: number = 20) {
  const { currentLanguage } = useLanguage();
  const { currentMarket, updateProductPrice } = useMarket();

  const { data, loading, error, refetch } = useQuery<ShopifyProductsResponse>(
    GET_PRODUCTS,
    {
      variables: {
        first: limit,
        language: getShopifyLanguageCode(currentLanguage),
        country: getShopifyCountryCode(currentMarket),
      },
      fetchPolicy: 'cache-and-network', // Always refetch when variables change
      notifyOnNetworkStatusChange: true,
    }
  );

  // Refetch when market changes
  useEffect(() => {
    logger.log(
      '🔄 useShopifyProducts: Market changed to',
      currentMarket,
      'for limit',
      limit
    );
    const variables = {
      first: limit,
      language: getShopifyLanguageCode(currentLanguage),
      country: getShopifyCountryCode(currentMarket),
    };
    logger.log('📡 Shopify GraphQL variables:', variables);
    refetch(variables).then(() => {
      logger.log('✅ Apollo refetch completed');
    });
  }, [currentMarket, currentLanguage, limit, refetch]);

  const products: Product[] = useMemo(() => {
    if (!data?.products?.edges) return [];

    return data.products.edges.map(edge => {
      if (edge.node && edge.node.priceRange) {
        const price = parseFloat(edge.node.priceRange.minVariantPrice.amount);
        logger.log('💰 Product price from Shopify:', {
          title: edge.node.title,
          amount: edge.node.priceRange.minVariantPrice.amount,
          currency: edge.node.priceRange.minVariantPrice.currencyCode,
          market: currentMarket,
        });
      }
      return convertShopifyProduct(edge.node);
    });
  }, [data?.products?.edges, currentMarket]);

  // Update product prices in context after data changes
  useEffect(() => {
    if (data?.products?.edges) {
      data.products.edges.forEach(edge => {
        if (edge.node && edge.node.priceRange) {
          const price = parseFloat(edge.node.priceRange.minVariantPrice.amount);
          logger.log(
            '💰 Context: Updating price for',
            edge.node.title,
            'to',
            price
          );
          updateProductPrice(edge.node.id, price);
        }
      });
    }
  }, [data]); // ТОЛЬКО data - updateProductPrice НЕ ДОЛЖЕН быть в dependencies!

  // Log when data changes
  useEffect(() => {
    if (data) {
      logger.log('🔄 Apollo data updated, products count:', products.length);
    }
  }, [data, products.length]);

  return {
    products,
    loading,
    error,
    hasProducts: products.length > 0,
  };
}

// Hook for getting a single product with language support
export function useShopifyProduct(handle: string) {
  const { currentLanguage } = useLanguage();
  const { currentMarket, updateProductPrice } = useMarket();

  const { data, loading, error, refetch } = useQuery<ShopifyProductResponse>(
    GET_PRODUCT,
    {
      variables: {
        handle,
        language: getShopifyLanguageCode(currentLanguage),
        country: getShopifyCountryCode(currentMarket),
      },
      skip: !handle,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    }
  );

  // Refetch when market changes
  useEffect(() => {
    if (handle) {
      logger.log(
        '🔄 useShopifyProduct: Market changed to',
        currentMarket,
        'for product',
        handle
      );
      const variables = {
        handle,
        language: getShopifyLanguageCode(currentLanguage),
        country: getShopifyCountryCode(currentMarket),
      };
      logger.log('📡 Shopify single product GraphQL variables:', variables);
      refetch(variables).then(() => {
        logger.log('✅ Apollo single product refetch completed');
      });
    }
  }, [currentMarket, currentLanguage, handle, refetch]);

  // Update single product price in context after data changes
  useEffect(() => {
    if (data?.product?.priceRange) {
      const price = parseFloat(data.product.priceRange.minVariantPrice.amount);
      logger.log(
        '💰 Context: Updating single product price for',
        data.product.title,
        'to',
        price
      );
      updateProductPrice(data.product.id, price);
    }
  }, [data]); // ТОЛЬКО data - updateProductPrice НЕ ДОЛЖЕН быть в dependencies!

  const product: Product | null = data?.product
    ? (() => {
        // Log single product price from Shopify
        if (data.product && data.product.priceRange) {
          logger.log('💰 Single product price from Shopify:', {
            title: data.product.title,
            amount: data.product.priceRange.minVariantPrice.amount,
            currency: data.product.priceRange.minVariantPrice.currencyCode,
            market: currentMarket,
            handle: handle,
          });
        }
        return convertShopifyProduct(data.product);
      })()
    : null;

  return {
    product,
    loading,
    error,
  };
}

// Hook for searching products with language support
export function useShopifyProductSearch(query: string, limit: number = 20) {
  const { currentLanguage } = useLanguage();

  // Convert our language codes to Shopify's LanguageCode enum
  const getShopifyLanguageCode = (lang: string) => {
    const langMap: Record<string, string> = {
      en: 'EN',
      de: 'DE',
      pl: 'PL',
      uk: 'UK',
    };
    return langMap[lang] || 'EN';
  };

  const { data, loading, error } = useQuery<ShopifyProductsResponse>(
    SEARCH_PRODUCTS,
    {
      variables: {
        query,
        first: limit,
        language: getShopifyLanguageCode(currentLanguage),
      },
      skip: !query || query.length < 3,
    }
  );

  const products: Product[] =
    data?.products?.edges?.map(edge => convertShopifyProduct(edge.node)) || [];

  return {
    products,
    loading,
    error,
    hasResults: products.length > 0,
  };
}

// Helper function to format price with currency
export function formatPrice(amount: string, currencyCode: string): string {
  const price = parseFloat(amount);

  switch (currencyCode) {
    case 'EUR':
      return `€${price.toFixed(2)}`;
    case 'PLN':
      return `${price.toFixed(2)} zł`;
    case 'USD':
      return `$${price.toFixed(2)}`;
    default:
      return `${price.toFixed(2)} ${currencyCode}`;
  }
}

// Helper to check if product is available
export function isProductAvailable(shopifyProduct: ShopifyProduct): boolean {
  return (
    shopifyProduct.variants?.edges?.some(edge => edge.node.availableForSale) ||
    false
  );
}

// Hook for loading ALL products with automatic pagination (500+ products) - with language support
export function useShopifyProductsPaginated() {
  const { currentLanguage } = useLanguage();
  const { updateProductPrice, currentMarket, getMarketCountryCode } =
    useMarket();
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false); // Separate state for background loading
  const [error, setError] = useState<any>(null);
  const [totalProductsLoaded, setTotalProductsLoaded] = useState(0);
  const [isLoadingComplete, setIsLoadingComplete] = useState(false);

  // Convert our language codes to Shopify's LanguageCode enum
  const getShopifyLanguageCode = (lang: string) => {
    const langMap: Record<string, string> = {
      en: 'EN',
      de: 'DE',
      pl: 'PL',
      uk: 'UK',
    };
    return langMap[lang] || 'EN';
  };

  // First page query - reduced to 30 for faster initial load
  const {
    data: firstPageData,
    loading: firstPageLoading,
    error: firstPageError,
  } = useQuery<ShopifyProductsPaginatedResponse>(GET_PRODUCTS_PAGINATED, {
    variables: {
      first: 30,
      language: getShopifyLanguageCode(currentLanguage),
      country: getMarketCountryCode(currentMarket),
    }, // Smaller first page for faster load
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network', // Always fetch from network
    notifyOnNetworkStatusChange: true, // Re-render on network status change
  });

  // Recursive function to load all remaining pages
  const loadRemainingPages = useCallback(
    async (currentProducts: Product[], cursor: string) => {
      try {
        const response = await fetch(
          `https://5pwxmm-ce.myshopify.com/api/2025-01/graphql.json`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token':
                import.meta.env.VITE_SHOPIFY_STOREFRONT_ACCESS_TOKEN ||
                '6dce028ac0d28efcb75de945d8a12666',
            },
            body: JSON.stringify({
              query: `
            query getProductsPaginated($first: Int!, $after: String, $language: LanguageCode, $country: CountryCode) @inContext(language: $language, country: $country) {
              products(first: $first, after: $after) {
                pageInfo {
                  hasNextPage
                  endCursor
                }
                edges {
                  cursor
                  node {
                    id
                    title
                    handle
                    description
                    priceRange {
                      minVariantPrice {
                        amount
                        currencyCode
                      }
                    }
                    images(first: 5) {
                      edges {
                        node {
                          url
                          altText
                        }
                      }
                    }
                    variants(first: 10) {
                      edges {
                        node {
                          id
                          title
                          availableForSale
                          quantityAvailable
                          price {
                            amount
                            currencyCode
                          }
                        }
                      }
                    }
                    tags
                    productType
                    vendor
                  }
                }
              }
            }
          `,
              variables: {
                first: 250,
                after: cursor,
                language: getShopifyLanguageCode(currentLanguage),
                country: getMarketCountryCode(currentMarket),
              },
            }),
          }
        );

        const result = await response.json();

        if (result.errors) {
          setError(result.errors[0]);
          setLoading(false);
          return;
        }

        const newProducts =
          result.data?.products?.edges?.map((edge: any) => {
            // Update price in context for paginated products
            if (edge.node && edge.node.priceRange) {
              const price = parseFloat(
                edge.node.priceRange.minVariantPrice.amount
              );
              updateProductPrice(edge.node.id, price);
            }
            const product = convertShopifyProduct(edge.node);
            // Log all products, and highlight anise-related products
            if (
              edge.node.title.toLowerCase().includes('anise') ||
              edge.node.handle.includes('anise')
            ) {
              // Anise product found - could add special handling here
            }
            return product;
          }) || [];

        const updatedProducts = [...currentProducts, ...newProducts];
        setAllProducts(updatedProducts);
        setTotalProductsLoaded(updatedProducts.length);

        // Check if there are more pages
        const hasNextPage = result.data?.products?.pageInfo?.hasNextPage;
        const nextCursor = result.data?.products?.pageInfo?.endCursor;

        if (hasNextPage && nextCursor) {
          // Continue loading next page
          setTimeout(
            () => loadRemainingPages(updatedProducts, nextCursor),
            100
          ); // Small delay to avoid rate limits
        } else {
          // All products loaded in background
          setLoadingMore(false);
          setIsLoadingComplete(true);
        }
      } catch (err) {
        setError(err);
        setLoadingMore(false);
      }
    },
    [
      currentLanguage,
      currentMarket,
      setError,
      setLoading,
      setAllProducts,
      setTotalProductsLoaded,
      setLoadingMore,
      setIsLoadingComplete,
    ]
  );

  useEffect(() => {
    if (firstPageError) {
      setError(firstPageError);
      setLoading(false);
      return;
    }

    if (!firstPageData || firstPageLoading) {
      return;
    }

    // Convert first page products
    const firstPageProducts =
      firstPageData.products?.edges?.map(edge => {
        // Update price in context for first page products
        if (edge.node && edge.node.priceRange) {
          const price = parseFloat(edge.node.priceRange.minVariantPrice.amount);
          updateProductPrice(edge.node.id, price);
        }
        return convertShopifyProduct(edge.node);
      }) || [];

    setAllProducts(firstPageProducts);
    setTotalProductsLoaded(firstPageProducts.length);

    // Show first page immediately - stop main loading
    setLoading(false);

    // Check if we need to load more pages in background
    const hasNextPage = firstPageData.products?.pageInfo?.hasNextPage;
    const endCursor = firstPageData.products?.pageInfo?.endCursor;

    if (hasNextPage && endCursor) {
      // Start background loading for remaining pages
      setLoadingMore(true);
      loadRemainingPages(firstPageProducts, endCursor);
    } else {
      setIsLoadingComplete(true);
    }
  }, [firstPageData, firstPageLoading, firstPageError, loadRemainingPages]);

  // Reset products when market changes - Apollo Client will automatically refetch
  useEffect(() => {
    logger.log(
      '🌍 useShopifyProductsPaginated: Market changed to',
      currentMarket,
      '- NOT clearing products to preserve scroll'
    );
    // DON'T clear products - let them stay until new ones load to preserve page height
    // setAllProducts([]);
    // setTotalProductsLoaded(0);
    setIsLoadingComplete(false);
    setLoading(true);
  }, [currentMarket]);

  return {
    products: allProducts,
    loading,
    loadingMore, // New state for background loading
    error,
    hasProducts: allProducts.length > 0,
    totalProductsLoaded,
    isLoadingComplete,
    // Helper methods
    getTotalCount: () => allProducts.length,
    isFullyCatalogLoaded: () => isLoadingComplete && allProducts.length > 0,
  };
}

// Simple hook to test blogs access with language support
export function useShopifyBlogs() {
  const { currentLanguage } = useLanguage();

  // Convert our language codes to Shopify's LanguageCode enum
  const getShopifyLanguageCode = (lang: string) => {
    const langMap: Record<string, string> = {
      en: 'EN',
      de: 'DE',
      pl: 'PL',
      uk: 'UK',
    };
    return langMap[lang] || 'EN';
  };

  const { data, loading, error } = useQuery<{
    blogs: { edges: { node: { id: string; title: string; handle: string } }[] };
  }>(GET_BLOGS, {
    variables: {
      language: getShopifyLanguageCode(currentLanguage),
    },
  });

  return {
    blogs: data?.blogs?.edges?.map((edge: any) => edge.node) || [],
    loading,
    error,
  };
}

// Hook for getting recipes from Shopify blog with language support
export function useShopifyRecipes(limit: number = 10) {
  const { currentLanguage } = useLanguage();

  // Convert our language codes to Shopify's LanguageCode enum
  const getShopifyLanguageCode = (lang: string) => {
    const langMap: Record<string, string> = {
      en: 'EN',
      de: 'DE',
      pl: 'PL',
      uk: 'UK',
    };
    return langMap[lang] || 'EN';
  };

  const { data, loading, error } = useQuery<ShopifyRecipesResponse>(
    GET_RECIPES,
    {
      variables: {
        first: limit,
        language: getShopifyLanguageCode(currentLanguage),
      },
    }
  );

  // Collect articles from all blogs
  const recipes =
    data?.blogs?.edges?.flatMap(
      blogEdge =>
        blogEdge.node?.articles?.edges?.map(articleEdge => articleEdge.node) ||
        []
    ) || [];

  return {
    recipes,
    loading,
    error,
    hasRecipes: recipes.length > 0,
  };
}

// Hook for getting a single recipe with language support
export function useShopifyRecipe(handle: string) {
  const { currentLanguage } = useLanguage();

  // Convert our language codes to Shopify's LanguageCode enum
  const getShopifyLanguageCode = (lang: string) => {
    const langMap: Record<string, string> = {
      en: 'EN',
      de: 'DE',
      pl: 'PL',
      uk: 'UK',
    };
    return langMap[lang] || 'EN';
  };

  const { data, loading, error } = useQuery<ShopifyRecipeResponse>(GET_RECIPE, {
    variables: {
      handle,
      language: getShopifyLanguageCode(currentLanguage),
    },
    skip: !handle,
  });

  // Search through all blogs to find the recipe
  const recipe =
    data?.blogs?.edges?.find(blogEdge => blogEdge.node?.articleByHandle)?.node
      ?.articleByHandle || null;

  return {
    recipe,
    loading,
    error,
  };
}

// Extended Recipe interface for RecipePage (with instructions and ingredients)
interface ExtendedRecipe extends Recipe {
  ingredients: Array<{
    id: string;
    name: string;
    amount: string;
    unit: string;
    notes?: string;
    spiceProductId?: string;
  }>;
  instructions: Array<{
    id: string;
    step: number;
    instruction: string;
    image?: string;
    timer?: number;
  }>;
  nutrition: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
    sodium: number;
  };
  videoUrl?: string;
}

// Convert Shopify article to ExtendedRecipe format for RecipePage - STRICT: only metafields, no fallbacks
export function convertShopifyToExtendedRecipe(
  article: ShopifyArticle
): ExtendedRecipe | null {
  const baseRecipe = convertShopifyArticleToRecipe(article);
  
  // If base recipe is null (missing essential metafields), return null
  if (!baseRecipe) {
    return null;
  }

  // Extract ingredients from metafields - REQUIRED
  const ingredientsMeta = getArticleMetafieldValue(article.metafields, 'custom', 'ingredients');
  if (!ingredientsMeta) {
    return null; // No ingredients = no recipe
  }
  
  let ingredients: ExtendedRecipe['ingredients'] = [];
  try {
    const parsed = JSON.parse(ingredientsMeta);
    ingredients = parsed.map((ing: any, index: number) => ({
      id: String(index + 1),
      name: ing.name || ing.ingredient || 'Ingredient',
      amount: String(ing.amount || '1'),
      unit: ing.unit || '',
      notes: ing.notes || '',
      spiceProductId: ing.spiceProductId || undefined,
    }));
  } catch {
    // Fallback: parse as simple string list
    const lines = ingredientsMeta.split('\n').filter(line => line.trim());
    if (lines.length === 0) return null;
    
    ingredients = lines.map((line, index) => {
      const match = line.match(/^(.+?)\s+(\d+(?:\.\d+)?)\s*(\w+)?\s*(.*)$/);
      return {
        id: String(index + 1),
        name: match ? match[1].trim() : line.trim(),
        amount: match ? match[2] : '1',
        unit: match ? match[3] || '' : '',
        notes: match ? match[4] || '' : '',
      };
    });
  }

  // Extract instructions from metafields - REQUIRED
  const instructionsMeta = getArticleMetafieldValue(article.metafields, 'custom', 'instructions');
  if (!instructionsMeta) {
    return null; // No instructions = no recipe
  }
  
  let instructions: ExtendedRecipe['instructions'] = [];
  try {
    const parsed = JSON.parse(instructionsMeta);
    instructions = parsed.map((inst: any, index: number) => ({
      id: String(index + 1),
      step: index + 1,
      instruction: inst.instruction || inst.text || inst.step || 'Follow the step',
      image: inst.image || undefined,
      timer: inst.timer ? parseInt(String(inst.timer)) : undefined,
    }));
  } catch {
    // Fallback: parse as simple string list
    const lines = instructionsMeta.split('\n').filter(line => line.trim());
    if (lines.length === 0) return null;
    
    instructions = lines.map((line, index) => ({
      id: String(index + 1),
      step: index + 1,
      instruction: line.trim(),
      timer: undefined,
    }));
  }

  // Extract nutrition from individual metafields
  const proteinMeta = getArticleMetafieldValue(article.metafields, 'custom', 'protein');
  const fatMeta = getArticleMetafieldValue(article.metafields, 'custom', 'fat');
  const carbsMeta = getArticleMetafieldValue(article.metafields, 'custom', 'carbs');
  
  const nutrition: ExtendedRecipe['nutrition'] = {
    calories: baseRecipe.calories,
    protein: proteinMeta ? parseInt(proteinMeta.replace(/[^\d]/g, '')) || 0 : 0,
    carbs: carbsMeta ? parseInt(carbsMeta.replace(/[^\d]/g, '')) || 0 : 0,
    fat: fatMeta ? parseInt(fatMeta.replace(/[^\d]/g, '')) || 0 : 0,
    fiber: 0, // Not available in our data
    sodium: 0, // Not available in our data
  };

  // No video URL in our current metafields
  const videoUrlMeta = null;

  return {
    ...baseRecipe,
    ingredients,
    instructions,
    nutrition,
    videoUrl: videoUrlMeta || undefined,
  };
}

// Hook for getting a single recipe formatted for RecipePage
export function useShopifyRecipeFormatted(handle: string) {
  const { recipe: rawRecipe, loading, error } = useShopifyRecipe(handle);

  const recipe: ExtendedRecipe | null = rawRecipe
    ? convertShopifyToExtendedRecipe(rawRecipe)
    : null;

  return {
    recipe,
    loading,
    error,
    hasRecipe: recipe !== null,
  };
}

// Hook for getting blog posts formatted for BlogSection component
export function useShopifyBlogPosts(limit: number = 10) {
  const { recipes, loading, error } = useShopifyRecipes(limit);

  // Convert Shopify articles to BlogPost format
  const blogPosts: BlogPost[] = recipes.map(article =>
    convertShopifyArticleToBlogPost(article)
  );

  return {
    blogPosts,
    loading,
    error,
    hasBlogPosts: blogPosts.length > 0,
  };
}

// Hook for getting recipes formatted for RecipesPage component
export function useShopifyRecipesFormatted(limit: number = 50) {
  const { recipes: rawRecipes, loading, error } = useShopifyRecipes(limit);

  // Convert Shopify articles to Recipe format - filter out null results
  const recipes: Recipe[] = rawRecipes
    .map(article => convertShopifyArticleToRecipe(article))
    .filter((recipe): recipe is Recipe => recipe !== null);

  return {
    recipes,
    loading,
    error,
    hasRecipes: recipes.length > 0,
  };
}

// Export interfaces for external use
export type { BlogPost, Recipe };
// Hook for getting all Shopify collections with language support
export function useShopifyCollections(first: number = 50) {
  const { currentLanguage } = useLanguage();

  // Convert our language codes to Shopify's LanguageCode enum
  const getShopifyLanguageCode = (lang: string) => {
    const langMap: Record<string, string> = {
      en: 'EN',
      de: 'DE',
      pl: 'PL',
      uk: 'UK',
    };
    return langMap[lang] || 'EN';
  };

  const { data, loading, error } = useQuery<ShopifyCollectionsResponse>(
    GET_COLLECTIONS,
    {
      variables: {
        first,
        language: getShopifyLanguageCode(currentLanguage),
      },
      errorPolicy: 'all',
    }
  );

  const collections = data?.collections?.edges?.map(edge => edge.node) || [];

  return {
    collections,
    loading,
    error,
    hasCollections: collections.length > 0,
  };
}

// Hook for getting products from a specific collection with language support
export function useCollectionProducts(handle: string, first: number = 50) {
  const { currentLanguage } = useLanguage();

  // Convert our language codes to Shopify's LanguageCode enum
  const getShopifyLanguageCode = (lang: string) => {
    const langMap: Record<string, string> = {
      en: 'EN',
      de: 'DE',
      pl: 'PL',
      uk: 'UK',
    };
    return langMap[lang] || 'EN';
  };

  const variables = {
    handle,
    first,
    after: null,
    language: getShopifyLanguageCode(currentLanguage),
  };
  
  const { data, loading, error } = useQuery<any>(GET_COLLECTION_PRODUCTS, {
    variables,
    skip: !handle,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  
  const collection = data?.collectionByHandle;
  
  const products =
    collection?.products?.edges?.map((edge: any) => {
      return convertShopifyProduct(edge.node);
    }) || [];

  return {
    collection: collection
      ? {
          id: collection.id,
          title: collection.title,
          description: collection.description,
          image: collection.image?.url,
        }
      : null,
    products,
    loading,
    error,
    hasProducts: products.length > 0,
  };
}

// Helper function to map menu categories to Shopify collections
export function mapMenuCategoryToCollection(categoryId: string): string | null {
  // Complete mapping of all 69 menu elements to Shopify collections
  const categoryMapping: Record<string, string> = {
    // Main categories (14)
    'popular-dishes': 'popular-dishes',
    'kitchen-essentials': 'kitchen-essentials',
    'spice-blends': 'spice-blends',
    'spices-herbs': 'spices-and-herbs',
    'hot-peppers-chili': 'hot-peppers-and-chili',
    'paprika': 'paprika',
    'organic': 'organic',
    'grill-bbq': 'for-grill-and-bbq',
    'gift-sets': 'gift-sets',
    'flavored-salts': 'flavored-salts',
    'world-cuisines': 'by-world-cuisines',
    'dish-type': 'by-dish-type',
    'baking': 'for-baking',
    'sales-discounts': 'sales-discounts',

    // Popular dishes items (10)
    'For Pizza': 'for-pizza',
    'For French Fries': 'for-french-fries',
    'For Kebab & BBQ Skewers': 'for-kebab-and-bbq-skewers',
    'For Pasta': 'for-pasta',
    'For Steak': 'for-steak',
    'For Fried Chicken': 'for-fried-chicken',
    'For Grilled Fish': 'for-grilled-fish',
    'For Roasted Potatoes': 'for-roasted-potatoes',
    'For Grilled Vegetables': 'for-grilled-vegetables',
    'For Soups': 'for-soups',

    // Kitchen essentials items (4)
    'Basic Spices': 'basic-spices',
    'Essential Herbs': 'essential-herbs',
    'Aromatic Bases': 'aromatic-bases',
    'Ready-Made Blends': 'ready-made-blends',

    // Spice blends items (4)
    'Regional Blends': 'regional-blends',
    'Universal Seasonings': 'universal-seasonings',
    'Chili Blends': 'chili-blends',
    'Smoked Blends': 'smoked-blends',

    // Spices & herbs subcategory items (5)
    'Mediterranean': 'mediterranean',
    'Asian': 'asian',
    'Indian': 'indian',
    'Latin American': 'latin-american',
    'African': 'african',

    // Hot peppers subcategory items (4)
    'Sweet & Mild (0-1000 Scoville)': 'sweet-and-mild-0-1000-scoville',
    'Moderately Hot (1000-10000 Scoville)': 'moderately-hot-1000-10000-scoville',
    'Hot (10000-100000 Scoville)': 'hot-10000-100000-scoville',
    'Very Hot (100000+ Scoville)': 'very-hot-100000-scoville',

    // Organic items (4)
    'Organic Spices': 'organic-spices',
    'Organic Herbs': 'organic-herbs',
    'Organic Blends': 'organic-blends',
    'Organic Chili & Peppers': 'organic-chili-and-peppers',

    // Grill & BBQ items (3)
    'Dry Rub Blends': 'dry-rub-blends',
    'Marinade Spices': 'marinade-spices',
    'Meat Blends': 'meat-blends',

    // Gift sets items (4)
    'Beginner Sets': 'beginner-sets',
    'Themed Sets': 'themed-sets',
    'Professional Sets': 'professional-sets',
    'Gift Sets': 'gift-sets-items',

    // World cuisines items (7)
    'Italian Cuisine': 'italian-cuisine',
    'Indian Cuisine': 'indian-cuisine',
    'Asian Cuisine': 'asian-cuisine',
    'Mexican Cuisine': 'mexican-cuisine',
    'Mediterranean Cuisine': 'mediterranean-cuisine',
    'French Cuisine': 'french-cuisine',
    'Middle Eastern Cuisine': 'middle-eastern-cuisine',

    // Dish type items (5)
    'Meat Dishes': 'meat-dishes',
    'Fish & Seafood': 'fish-and-seafood',
    'Vegetable Dishes': 'vegetable-dishes',
    'Soups & Broths': 'soups-and-broths',
    'Salads & Appetizers': 'salads-and-appetizers',

    // Baking items (3)
    'Sweet Spices': 'sweet-spices',
    'Spice Blends': 'spice-blends-baking',
    'Aromatic Additives': 'aromatic-additives',
  };

  return categoryMapping[categoryId] || null;
}

// Hook for getting products from multiple collections
// Rules of Hooks compliant approach: use fixed hooks with conditional logic
export function useMultipleCollectionProducts(handles: string[], first: number = 250) {
  const [combinedProducts, setCombinedProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState<any>(null);
  
  // Get current language for consistent queries
  const { currentLanguage } = useLanguage();

  // Use Apollo's useQuery directly with proper dependency management
  const { data, loading, error } = useQuery<any>(GET_COLLECTION_PRODUCTS, {
    variables: {
      handle: handles[0] || '', // Use first handle or empty string
      first: Math.floor(first / Math.max(handles.length, 1)),
      after: null,
      language: getShopifyLanguageCode(currentLanguage),
    },
    skip: handles.length === 0,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (handles.length === 0) {
      setCombinedProducts([]);
      setIsLoading(false);
      setHasError(null);
      return;
    }

    if (handles.length === 1) {
      // Single collection case - use direct query result
      setIsLoading(loading);
      setHasError(error);
      
      if (!loading && data?.collectionByHandle?.products) {
        const products = data.collectionByHandle.products.edges.map((edge: any) => {
          return convertShopifyProduct(edge.node);
        });
        setCombinedProducts(products);
      } else if (!loading) {
        setCombinedProducts([]);
      }
    } else {
      // Multiple collections case - fetch sequentially to avoid Rules of Hooks
      setIsLoading(true);
      
      const fetchAllCollections = async () => {
        const allProducts: Product[] = [];
        let hasErrors = false;
        
        for (const handle of handles) {
          try {
            const result = await shopifyClient.query({
              query: GET_COLLECTION_PRODUCTS,
              variables: {
                handle,
                first: Math.floor(first / handles.length),
                after: null,
                language: getShopifyLanguageCode(currentLanguage),
              },
              errorPolicy: 'all',
            });
            
            if (result.data?.collectionByHandle?.products?.edges) {
              const products = result.data.collectionByHandle.products.edges.map((edge: any) => {
                return convertShopifyProduct(edge.node);
              });
              allProducts.push(...products);
            }
          } catch (err) {
            console.error(`Failed to fetch collection ${handle}:`, err);
            hasErrors = true;
          }
        }
        
        // Remove duplicates by ID
        const uniqueProducts = allProducts.filter((product, index, arr) => 
          arr.findIndex(p => p.id === product.id) === index
        );
        
        setCombinedProducts(uniqueProducts);
        setIsLoading(false);
        setHasError(hasErrors ? new Error('Some collections failed to load') : null);
      };
      
      fetchAllCollections();
    }
  }, [handles.join(','), first, currentLanguage, data, loading, error]);

  return {
    products: combinedProducts,
    loading: isLoading,
    error: hasError,
    hasProducts: combinedProducts.length > 0,
  };
}
